import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { clsx, type ClassValue } from 'clsx'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

const buttonVariants = cva(
	'group relative inline-flex items-center justify-center font-bold transition-all rounded-full overflow-hidden border-2 select-none focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
	{
		variants: {
			variant: {
				primary:
					'border-slate-300 bg-slate-900 text-white hover:border-brand-blue focus:ring-brand-blue',
				outline:
					'border-brand-blue text-brand-blue hover:bg-slate-100 focus:ring-brand-blue',
				ghost:
					'border-transparent text-slate-700 hover:bg-slate-100 focus:ring-slate-400',
			},
			size: {
				sm: 'h-9 px-4 text-xs gap-1.5',
				md: 'h-11 px-6 text-sm gap-2',
				lg: 'h-14 px-8 text-base gap-3',
				icon: 'h-11 w-11 p-0',
			},
			fullWidth: {
				true: 'w-full',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'md',
		},
	},
)

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	isLoading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant = 'primary',
			size,
			fullWidth,
			isLoading,
			children,
			disabled,
			...props
		},
		ref,
	) => {
		const isWaterVariant = variant === 'primary'

		return (
			<button
				ref={ref}
				disabled={disabled || isLoading}
				className={cn(buttonVariants({ variant, size, fullWidth }), className)}
				{...props}>
				{/* SURGING WATER FLOW CONTAINER */}
				{/* Rushes in from the left on hover (-translate-x-full to translate-x-0) */}
				{isWaterVariant && !isLoading && (
					<div className='absolute inset-0 z-0 -translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0 pointer-events-none overflow-hidden'>
						<div className='h-full w-[200%] animate-waterFlow flex'>
							<svg
								viewBox='0 0 200 40'
								preserveAspectRatio='none'
								className='h-full w-full fill-brand-blue opacity-90'>
								<path d='M0 20 Q 25 8, 50 20 T 100 20 T 150 20 T 200 20 V 40 H 0 Z' />
							</svg>
						</div>
					</div>
				)}

				{/* BUTTON CONTENT */}
				<span className='relative z-10 flex items-center justify-center gap-2'>
					{isLoading ? (
						<>
							<svg
								className='animate-spin h-4 w-4 text-current'
								viewBox='0 0 24 24'
								fill='none'>
								<circle
									className='opacity-25'
									cx='12'
									cy='12'
									r='10'
									stroke='currentColor'
									strokeWidth='4'
								/>
								<path
									className='opacity-75'
									fill='currentColor'
									d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
								/>
							</svg>
							Loading...
						</>
					) : (
						children
					)}
				</span>
			</button>
		)
	},
)

Button.displayName = 'Button'
